/**
 * Centralized helper for patching the payment summary after addon/seat
 * changes during the upgrade-to-premium flow.
 *
 * Problem: the initial upgrade summary (built by PrepareBookingSummaryAction
 * with isUpgrade=true) correctly computes fare delta, modification/exchange
 * fees, and alreadyPaid. Re-running prepareBookingSummaryAction after
 * addon/seat mutations would destroy that fare structure because the re-run
 * lacks EXCHANGE_ONGOING admissions needed for subtraction.
 *
 * Solution: instead of re-running the full summary, we read the PREBOOKED
 * offers from the fresh booking, compute addon + seat fee line items using
 * the same logic as PrepareBookingSummaryAction's accumulators, then patch
 * them into the existing checkoutPaymentSummaryAtom — preserving all fare,
 * exchange-fee, and alreadyPaid values intact.
 */

import { calculatePrice, isInbound, isOutbound } from "../../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { AncillaryStatusEnum, TrainTypeEnum } from "../../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookedOffer, IBooking, IBookingTypeTrip, IPassenger } from "../../../../Orchestrator/Types/Index";
import { buildCarriedOverAncillaryKeys, isCarriedOverAncillary } from "../../../../Lib/Utils/upgradeAncillaryTwins";
import { isNolAncillary } from "../../../../Lib/Utils/nolCard";

// Fee types already handled in the fare calculation (exchange/modification fees).
const HANDLED_FEE_TYPES = new Set(["SALES_FEE", "MODIFICATION_FEE", "EXCHANGE_FEE"]);

/** Sum active (non-canceled, non-pending-refund) addon amounts from the otherDetails.addons structure. */
function sumActiveAddons(addons: Record<string, { amount: number; description: string }[]> | undefined): number {
  let total = 0;
  Object.values(addons || {}).forEach((items) => {
    items.forEach((item) => {
      const desc = item.description || "";
      if (!desc.includes("(Canceled)") && !desc.includes("(Refund)")) {
        total += item.amount || 0;
      }
    });
  });
  return total;
}

/** Sum seat fee amounts from the otherDetails.seatFees structure (skip Canceled items). */
function sumSeatFees(seatFees: Record<string, { amount: number; description: string }[]> | undefined): number {
  let total = 0;
  Object.values(seatFees || {}).forEach((items) => {
    items.forEach((item) => {
      if (!item.description?.includes("(Canceled)")) total += item.amount || 0;
    });
  });
  return total;
}

/**
 * What the Nol passes on this summary are worth.
 *
 * A Nol pass is neither an addon nor a seat fee — PrepareBookingSummaryAction
 * prices it on its own rows (`passengerDetails.nolPassengers`) and adds it
 * straight to `payment.total`. This merge rebuilds the total from
 * `baseFare + addons + seatFees`, so without this term the pass's money
 * disappeared on the next seat or addon confirm while its ROW survived the
 * `...currentSummary` spread: the receipt read "Adult 99 / 1 Gold Nol card 40 /
 * Total 60". Whether it happened at all depended on when the pass was added
 * relative to the first merge, which is why it came and went.
 *
 * Refunded rows are excluded for the same reason PrepareBookingSummaryAction
 * excludes them: they are not charged again.
 */
function sumNolRows(summary: any): number {
  return sumOwnRows(summary, "nolPassengers");
}

/**
 * The onward-shuttle money on the summary's own rows.
 *
 * Same shape as sumNolRows and there for the same reason: a shuttle is neither
 * an addon nor a seat fee, it is priced on `passengerDetails.shuttlePassengers`
 * and reaches `payment.total` through `${direction}SuccessTotalPrice`. This
 * merge rebuilds the total from `baseFare + addons + seatFees`, so without this
 * term the shuttle's fare disappeared on the next seat or addon confirm while
 * its row survived the `...currentSummary` spread.
 */
function sumShuttleRows(summary: any): number {
  return sumOwnRows(summary, "shuttlePassengers");
}

function sumOwnRows(summary: any, rowKey: "nolPassengers" | "shuttlePassengers"): number {
  let total = 0;
  for (const direction of ["outbound", "inbound"]) {
    for (const row of summary?.[direction]?.passengerDetails?.[rowKey] || []) {
      if (!row?.isRefunded) total += row?.amount || 0;
    }
  }
  return total;
}

/**
 * Compute addon line items from PREBOOKED offers (mirrors PrepareBookingSummaryAction addon accumulator).
 *
 * Ancillaries arrive from the backend in an arbitrary order. The payment
 * summary above lists passengers in booking.passengers order (adult, then
 * senior, etc.), so the addon rows must sort by the owning passenger's
 * position in that list. When a category groups ancillaries across multiple
 * passengers, the earliest owner wins — keeps the adult's rows above the
 * senior's even if they share a category.
 */
function computeAddonsFromOffers(
  offers: IBookedOffer[],
  trips: IBookingTypeTrip[],
  passengers: IPassenger[],
  carriedOverKeys: Set<string>,
  allProducts: IBookedOffer["products"],
): Record<string, { amount: number; description: string }[]> {
  const passengerIdxById = new Map<string, number>(
    (passengers || []).map((p, idx) => [p.id, idx] as const),
  );

  type Bucket = { amount: number; count: number; minPassengerIdx: number };
  const accumulator: Record<string, {
    active: Record<string, Bucket>;
    canceled: Record<string, Bucket>;
  }> = {};

  offers.forEach((offer) => {
    const trip = trips.find((t) => t.id === offer?.tripCoverage?.coveredTripId);
    if (!trip) return;
    const direction = isInbound(trip.direction) ? TrainTypeEnum.inbound : TrainTypeEnum.outbound;
    const ancillaries = (offer.ancillaries || []).filter(
      (a: any) => a?.tripCoverage?.coveredTripId === trip.id,
    );

    if (!accumulator[direction]) {
      accumulator[direction] = { active: {}, canceled: {} };
    }

    for (const anc of ancillaries) {
      // NOL passes belong to the End-to-end Journey flow, not the Extras total —
      // never fold a Day Pass into the upgrade addon accumulator/credit.
      if (isNolAncillary(allProducts, anc)) continue;
      // Skip ancillaries the backend auto-linked from the original booking.
      // They appear here as fresh PREBOOKED entries on the upgrade offer but
      // were already paid for; counting them would double-charge the user.
      if (isCarriedOverAncillary(anc as any, carriedOverKeys)) continue;
      const ancPassengerId: string | undefined = (anc as any)?.passengerIds?.[0];

      const productId = anc.products[0]?.productId;
      const product = offer.products?.find((p: any) => p.id === productId);
      const category = (product as any)?.description || "Add-on";
      const statusKey = anc.status === AncillaryStatusEnum.REFUNDED ? "canceled" : "active";
      const target = accumulator[direction][statusKey];
      const idx = ancPassengerId !== undefined ? passengerIdxById.get(ancPassengerId) : undefined;
      const passengerIdx = typeof idx === "number" ? idx : Number.MAX_SAFE_INTEGER;
      if (!target[category]) {
        target[category] = { amount: 0, count: 0, minPassengerIdx: passengerIdx };
      } else {
        target[category].minPassengerIdx = Math.min(target[category].minPassengerIdx, passengerIdx);
      }
      target[category].amount += calculatePrice(anc.price);
      target[category].count += 1;
    }
  });

  const sortByPassengerOrder = (entries: Array<[string, Bucket]>): Array<[string, Bucket]> =>
    [...entries].sort(([, a], [, b]) => a.minPassengerIdx - b.minPassengerIdx);

  const result: Record<string, { amount: number; description: string }[]> = {};
  Object.keys(accumulator).forEach((direction) => {
    const { active, canceled } = accumulator[direction];
    const activeItems = sortByPassengerOrder(Object.entries(active)).map(([cat, v]) => ({
      amount: v.amount,
      description: `${v.count} ${cat}`,
    }));
    const canceledItems = sortByPassengerOrder(Object.entries(canceled)).map(([cat, v]) => ({
      amount: v.amount,
      description: `${v.count} ${cat} (Canceled)`,
    }));
    result[direction] = [...activeItems, ...canceledItems];
  });
  return result;
}

/** Compute seat-fee line items from PREBOOKED offers (mirrors PrepareBookingSummaryAction seat accumulator). */
function computeSeatFeesFromOffers(
  offers: IBookedOffer[],
  trips: IBookingTypeTrip[],
): Record<string, { amount: number; description: string }[]> {
  const accumulator: Record<string, Record<string, { amount: number; count: number }>> = {};

  offers.forEach((offer) => {
    const trip = trips.find((t) => t.id === offer?.tripCoverage?.coveredTripId);
    if (!trip) return;
    const direction = isInbound(trip.direction) ? TrainTypeEnum.inbound : TrainTypeEnum.outbound;
    if (!accumulator[direction]) accumulator[direction] = {};

    for (const reservation of (offer.reservations || [])) {
      const status = ((reservation as any)?.status || "").toUpperCase();
      if (status === "FULFILLED" || status === "FULLFILLED" || status === "REFUNDED") continue;
      const price = calculatePrice(reservation.price);
      if (price <= 0) continue;

      const productId = reservation.products?.[0]?.productId;
      const product = (offer.products as any[])?.find((p: any) => p.id === productId);
      const category = (product as any)?.description || "Seat";
      if (!accumulator[direction][category]) accumulator[direction][category] = { amount: 0, count: 0 };
      accumulator[direction][category].amount += price;
      accumulator[direction][category].count += 1;
    }

    // Capture non-handled fee types (e.g. PLACE_FEE, SEAT_FEE) but exclude
    // exchange/modification/sales fees and any fee whose summary mentions "exchange".
    for (const fee of ((offer as any)?.fees || [])) {
      if (!fee?.price) continue;
      if (HANDLED_FEE_TYPES.has(fee?.type)) continue;
      if ((fee?.summary || "").toLowerCase().includes("exchange")) continue;
      if ((fee?.summary || "").toLowerCase().includes("modification")) continue;

      const feePrice = calculatePrice(fee.price);
      if (feePrice <= 0) continue;
      const category = fee.summary || fee.description || fee.type || "Seat Fee";
      if (!accumulator[direction]) accumulator[direction] = {};
      if (!accumulator[direction][category]) accumulator[direction][category] = { amount: 0, count: 0 };
      accumulator[direction][category].amount += feePrice;
      accumulator[direction][category].count += 1;
    }
  });

  const result: Record<string, { amount: number; description: string }[]> = {};
  Object.keys(accumulator).forEach((direction) => {
    const entries = accumulator[direction];
    result[direction] = Object.entries(entries).map(([cat, v]) => ({
      amount: v.amount,
      description: `${v.count} ${cat}`,
    }));
  });
  return result;
}

/**
 * Recompute outbound/inbound exchange fees from booking.exchangeOperations.
 * Used in mergeUpgradePaymentSummary to restore fees that may have been lost
 * from the atom after addon confirmation (Bug: exchange fee removed on addon add).
 * Handles both TripTypeEnum ("OUT_BOUND"/"IN_BOUND") and TripDirectionTypeEnum
 * ("OUTBOUND"/"INBOUND") formats returned by different API endpoints.
 */
function computeExchangeFeesFromBooking(booking: IBooking): { outboundFee: number | undefined; inboundFee: number | undefined } {
  const ops: any[] = (booking as any).exchangeOperations || [];
  if (ops.length === 0) return { outboundFee: undefined, inboundFee: undefined };

  const allOffers: any[] = [];
  const allTrips: any[] = [];
  ops.forEach((op: any) => {
    if (Array.isArray(op?.exchangeOffers)) allOffers.push(...op.exchangeOffers);
    if (Array.isArray(op?.trips)) allTrips.push(...op.trips);
  });

  const seen = new Set<string>();
  const uniqueTrips = allTrips.filter((t: any) => {
    if (!t?.id || seen.has(t.id)) return false;
    seen.add(t.id);
    return true;
  });

  const feeForDirection = (check: (d: string) => boolean): number | undefined => {
    const tripIds = new Set<string>(
      uniqueTrips
        .filter((t: any) => check(t?.direction))
        .map((t: any) => t.id)
        .filter(Boolean),
    );
    if (tripIds.size === 0) return undefined;
    const matched = allOffers.filter(
      (o: any) => o?.tripCoverage?.coveredTripId && tripIds.has(o?.tripCoverage?.coveredTripId) && o?.exchangeFee,
    );
    if (matched.length === 0) return undefined;
    return matched.reduce((sum: number, o: any) => sum + calculatePrice(o.exchangeFee), 0);
  };

  return {
    outboundFee: feeForDirection(isOutbound),
    inboundFee: feeForDirection(isInbound),
  };
}

/**
 * Patch the existing upgrade payment summary with fresh addon/seat data
 * from the booking, WITHOUT re-running the full PrepareBookingSummaryAction.
 *
 * @param booking - The freshly fetched booking (all offers, including FULFILLED)
 */
export function mergeUpgradePaymentSummary(booking: IBooking): void {
  const currentSummary: any = getRecoilStateValue("checkoutPaymentSummaryAtom");
  if (!currentSummary) return;

  // Filter to PREBOOKED offers only (new upgrade offers).
  const prebookedOffers = (booking.bookedOffers || []).filter(
    (offer: IBookedOffer) => offer.admissions?.some((a) => a.status === "PREBOOKED"),
  );
  const prebookedTripIds = new Set(
    prebookedOffers.map((o) => o.tripCoverage?.coveredTripId).filter(Boolean),
  );
  const prebookedTrips = (booking.trips || []).filter(
    (t: IBookingTypeTrip) => prebookedTripIds.has(t.id),
  );

  // Compute old addon/seat totals from the current summary.
  // These were set either by PrepareBookingSummaryAction (isUpgrade=true, first call)
  // or by a prior mergeUpgradePaymentSummary (subsequent calls). Both only include
  // PREBOOKED ancillaries, so subtracting them is safe.
  const oldAddonTotal = sumActiveAddons(currentSummary.otherDetails?.addons);
  const oldSeatFeeTotal = sumSeatFees(currentSummary.otherDetails?.seatFees);
  // This merge never touches the Nol or shuttle rows, so one reading of each
  // serves both ends: it comes OUT of the derived base fare and goes back ON to
  // the new total.
  const nolTotal = sumNolRows(currentSummary);
  const shuttleTotal = sumShuttleRows(currentSummary);

  // baseFareTotal is the fare-only portion of the upgrade (no addons/seats).
  // It does not change across addon/seat mutations — only the trip itself
  // changes it (which triggers PrepareBookingSummaryAction, not this merge).
  // Persist it on the atom so subsequent merges read the fare directly instead
  // of re-deriving it from a possibly-stale total.
  let baseFareTotal: number;
  if (typeof currentSummary.payment?.upgradeBaseFareTotal === "number") {
    baseFareTotal = currentSummary.payment.upgradeBaseFareTotal;
  } else {
    // First merge: derive from current.total (set by PrepareBookingSummaryAction
    // and possibly augmented by ConfirmAddonsAction with addon line items).
    // Guard against underflow: if the summary atom was not reset at upgrade-flow
    // entry and contains stale FULFILLED-era addon data, the subtraction can go
    // negative. Math.max(0, ...) prevents a negative total reaching the UI.
    const rawBaseFare =
      (currentSummary.payment?.total || 0) - oldAddonTotal - oldSeatFeeTotal - nolTotal - shuttleTotal;
    if (rawBaseFare < 0) {
      console.warn(
        "[mergeUpgradePaymentSummary] baseFareTotal underflow: payment.total < old addon+seat totals. " +
        "The checkoutPaymentSummaryAtom may contain stale data from a prior non-upgrade flow. " +
        `payment.total=${currentSummary.payment?.total}, oldAddonTotal=${oldAddonTotal}, oldSeatFeeTotal=${oldSeatFeeTotal}`
      );
    }
    baseFareTotal = Math.max(0, rawBaseFare);
  }

  // Compute fresh addon/seat data from PREBOOKED offers.
  // Build the carry-over key set once so the addon accumulator can skip
  // backend-auto-linked ancillaries (see upgradeAncillaryTwins doc).
  const carriedOverKeys = buildCarriedOverAncillaryKeys(booking);
  // Flatten once: post-exchange the NOL product an ancillary references can
  // live on a sibling offer, not just the offer that owns it.
  const allProducts = (booking.bookedOffers || []).flatMap((o) => o?.products || []);
  const freshAddons = computeAddonsFromOffers(prebookedOffers, prebookedTrips, booking.passengers || [], carriedOverKeys, allProducts);
  const freshSeatFees = computeSeatFeesFromOffers(prebookedOffers, prebookedTrips);
  const newAddonTotal = sumActiveAddons(freshAddons);
  const newSeatFeeTotal = sumSeatFees(freshSeatFees);

  // Recompute exchange fees from booking.exchangeOperations so they are always
  // up-to-date even if the atom previously lost them (e.g. after addon confirm).
  const { outboundFee, inboundFee } = computeExchangeFeesFromBooking(booking);

  // Build fresh outbound/inbound objects: start from currentSummary values (preserves
  // passenger breakdowns) and overlay the recomputed exchange fee where available.
  const patchedOutbound = currentSummary.outbound
    ? {
        ...currentSummary.outbound,
        passengerDetails: {
          ...currentSummary.outbound.passengerDetails,
          ...(outboundFee !== undefined ? { exchangeFee: outboundFee } : {}),
        },
      }
    : currentSummary.outbound;

  const patchedInbound = currentSummary.inbound
    ? {
        ...currentSummary.inbound,
        passengerDetails: {
          ...currentSummary.inbound.passengerDetails,
          ...(inboundFee !== undefined ? { exchangeFee: inboundFee } : {}),
        },
      }
    : currentSummary.inbound;

  // Merge: keep all fare/passenger/exchange structure, overlay addon/seat data.
  // Invariant: subTotal = total + alreadyPaid (total is what the customer still
  // owes; subTotal is the gross booking cost including the pre-paid portion).
  //
  // Carried-over addons from the original booking are surfaced as read-only
  // locked rows in the modal (see carriedOverAddonsAtom). Their value stays
  // attached to the original booking — newly-added addons here are charged in
  // full on top of the upgrade fare, while the backend-auto-linked twins on
  // the PREBOOKED upgrade offer are excluded from `newAddonTotal` via the
  // twin filter in computeAddonsFromOffers. Persist upgradeBaseFareTotal so
  // subsequent merges read the fare directly instead of re-deriving it.
  const newNetTotal = baseFareTotal + newAddonTotal + newSeatFeeTotal + nolTotal + shuttleTotal;
  const alreadyPaid = currentSummary.payment?.alreadyPaid || 0;
  updateRecoilState("checkoutPaymentSummaryAtom", {
    ...currentSummary,
    ...(patchedOutbound !== undefined ? { outbound: patchedOutbound } : {}),
    ...(patchedInbound !== undefined ? { inbound: patchedInbound } : {}),
    otherDetails: {
      ...currentSummary.otherDetails,
      addons: freshAddons,
      seatFees: Object.keys(freshSeatFees).length > 0 ? freshSeatFees : {},
    },
    payment: {
      ...currentSummary.payment,
      total: newNetTotal,
      subTotal: newNetTotal + alreadyPaid,
      upgradeBaseFareTotal: baseFareTotal,
    },
  });
}
